import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import { useSchedules } from "hooks/useSchedules";
import schedule from "assets/images/schedule.svg";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import ExecutionsCalendar from "layouts/Flow Designer/components/ExecutionsCalendar";
import TileView from "layouts/Flows/components/TileView";
import ScheduleMenu from "layouts/pages/library/schedules/components/ScheduleMenu";
import SchedulerDialog from "layouts/Flow Designer/components/ExecutionsCalendar/components/SchedulerDialog";
import ShareScheduleDialog from "layouts/pages/library/schedules/components/ShareScheduleDialog";
import UnshareScheduleDialog from "layouts/pages/library/schedules/components/UnshareScheduleDialog";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useResourceListener } from "hooks/useResourceListener";

const options = {
   status: [
      { value: "Active", label: "Active" },
      { value: "Recurring", label: "Recurring" },
      { value: "Paused", label: "Paused" }
   ]
};

const Schedules = () => {
   const [openSchedulerDialog, setOpenSchedulerDialog] = useState(false);
   const [openShareScheduleDialog, setOpenShareScheduleDialog] = useState(false);
   const [openUnshareScheduleDialog, setOpenUnshareScheduleDialog] = useState(false);
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [tableView, setTableView] = useState("list");
   const { reloadSchedules, reloadSharedSchedules, ownedSharedSchedules, determineShareStatus } = useSchedules();
   const { flows } = usePortalFlows();
   const { user } = useAuth0();
   const navigate = useNavigate();
   useResourceListener("schedulesSharingUpdate", [reloadOwnedSharedSchedules, reloadSchedules]);

   const handleFlowView = (view) => {
      setTableView(view);
      localStorage.setItem("flowView", view);
   };

   const handleOpenScheduleDetail = (id) => {
      navigate(`/schedule/${id}`);
   };

   const handleCloseSchedulerDialog = () => {
      setOpenSchedulerDialog(false);
      setSelectedEvent(null);
   };

   const handleCloseShareScheduleDialog = () => {
      setOpenShareScheduleDialog(false);
      setSelectedEvent(null);
   };

   const handleCloseUnshareScheduleDialog = () => {
      setOpenUnshareScheduleDialog(false);
      setSelectedEvent(null);
   };

   function reloadOwnedSharedSchedules() {
      reloadSchedules();
      reloadSharedSchedules();
   }

   const dataTableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "scheduleId",
               accessor: "scheduleId",
               show: false
            },
            {
               Header: "icon",
               accessor: "icon",
               disableSortBy: true,
               Cell: ({ value }) => {
                  return (
                     <SoftBox width="60px">
                        <SoftBox
                           component="img"
                           width="100%"
                           minHeight="60px"
                           border="0.5px solid #E6E6E6"
                           borderRadius="12px"
                           sx={{
                              objectFit: "cover"
                           }}
                           src={value || schedule}
                        />
                     </SoftBox>
                  );
               }
            },
            {
               Header: "name",
               accessor: "nameLink",
               width: 250,
               Cell: ({ value }) => (
                  <SoftBox
                     className="white-to-dark"
                     fontSize="14px"
                     fontWeight="bold"
                     display="flex"
                     alignItems="center"
                     sx={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        whiteSpace: "normal",
                        cursor: "pointer",
                        wordBreak: "break-all"
                     }}
                     onClick={() => handleOpenScheduleDetail(value?.id)}
                  >
                     {value?.name?.length > 24 ? value?.name?.slice(0, 24) + "..." : value?.name}
                  </SoftBox>
               )
            },
            {
               Header: "name",
               accessor: "name",
               show: false
            },
            {
               Header: "status",
               accessor: "status",
               Cell: ({ value }) => {
                  return <StatusBadge status={value} />;
               }
            },
            {
               Header: "shared status",
               accessor: "shared_status",
               Cell: ({ value }) => <SoftTypography fontSize="14px">{value}</SoftTypography>
            },
            {
               Header: "last edited",
               accessor: "lastEdited",
               width: 170,
               disableSortBy: false,
               defaultSortOrder: "desc",
               sortType: (rowA, rowB, columnId) => {
                  const dateA = new Date(rowA.original.lastEditedTimestamp || rowA.values[columnId]);
                  const dateB = new Date(rowB.original.lastEditedTimestamp || rowB.values[columnId]);
                  return dateA - dateB;
               }
            },
            {
               Header: "actions",
               accessor: "action",
               canGroupBy: false,
               disableSortBy: true
            }
         ],
         rows: [
            ...[...ownedSharedSchedules]
               .sort((firstSchedule, secondSchedule) => new Date(secondSchedule.updated_date_time || secondSchedule.created_date_time || 0) - new Date(firstSchedule.updated_date_time || firstSchedule.created_date_time || 0))
               .map((event) => {
               return {
                  scheduleId: event.id,
                  icon: schedule,
                  nameLink: { name: event.title, id: event.id },
                  name: event.title,
                  status: event.status?.toLowerCase(),
                  shared_status: determineShareStatus(event),
                  lastEditedTimestamp: event.updated_date_time || event.created_date_time || null,
                  lastEdited: formatDateAndTime(getLocalDateTimeString(event.updated_date_time || event.created_date_time)) || "",
                  action: (
                     <ScheduleMenu
                        item={event}
                        user={user}
                        setOpenSchedulerDialog={setOpenSchedulerDialog}
                        setSelectedEvent={setSelectedEvent}
                        setOpenShareScheduleDialog={setOpenShareScheduleDialog}
                        setOpenUnshareScheduleDialog={setOpenUnshareScheduleDialog}
                        flows={flows}
                     />
                  )
               };
            })
         ]
      };
   }, [ownedSharedSchedules]);

   return (
      <SoftBox display="flex" flexDirection="column">
         <SoftBox flexGrow={1}>
            <Card
               sx={{
                  overflow: "visible",
                  boxShadow: 0,
                  backgroundColor: "transparent !important"
               }}
            >
               <DataTable
                  table={dataTableData}
                  entriesPerPage={{ defaultValue: 25 }}
                  hiddenTopButtons={false}
                  tableView={tableView}
                  setTableView={handleFlowView}
                  splitted={true}
                  withSearchbar={true}
                  withTileview={true}
                  withCalendarView={true}
                  withStatusFilter={true}
                  options={options}
                  tileViewComponent={(rows) => <TileView data={rows} groupBy={[]} />}
                  calendarViewComponent={
                     <SoftBox p={3} className="calendar-wrapper">
                        <ExecutionsCalendar />
                     </SoftBox>
                  }
                  reloadTableData={reloadOwnedSharedSchedules}
                  withScheduleOptions
                  setOpenSchedulerDialog={setOpenSchedulerDialog}
                  tableHeight="clamp(400px, calc(100vh - 83px), calc(100vh - 83px))"
               />
               <SchedulerDialog open={openSchedulerDialog} selectedEvent={selectedEvent} closeDialog={handleCloseSchedulerDialog} />
               <ShareScheduleDialog open={openShareScheduleDialog} onClose={handleCloseShareScheduleDialog} schedule={selectedEvent} reloadOwnedSharedSchedules={reloadOwnedSharedSchedules} />
               <UnshareScheduleDialog open={openUnshareScheduleDialog} onClose={handleCloseUnshareScheduleDialog} schedule={selectedEvent} reloadOwnedSharedSchedules={reloadOwnedSharedSchedules} />
            </Card>
         </SoftBox>
      </SoftBox>
   );
};

export default Schedules;
